import React, {useEffect, useState} from "react";
import {Template} from "../../../../components/template/template.jsx";
import {Card} from "../../../../components/card/card.jsx";
import Button from "../../../../components/button/button.jsx";
import {useParams, useSearchParams} from "react-router";
import {FaPlus} from "react-icons/fa";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import maintenancesService from "../../../../services/web/maintenances-service.js";
import SentryRow from "../../../../components/template/sentry-row.jsx";
import InputDatetime from "../../../../components/input/input-datetime.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import useFetchData from "./hooks/use-fetch-data.js";
import InputForm from "../../../../components/input/input-form.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import InputTextarea from "../../../../components/input/input-textarea.jsx";
import InputFile from "../../../../components/input/input-file.jsx";
import projectUtils from "../../../../utils/project-utils.js";
import Alert
    from "../../../../components/alert/alert.jsx";

const initialState = {
    mechanic_file_attach: null,
    vehicles_id: null,
    conductor: null,
    date_start: "",
    date_end: "",
    value_pay: "",
    km_start: "",
    km_end: "",
    description: "",
    mechanic_file: null,
    mechanics: null,
    schedule_maintenance: false,
    schedule_cancel: false,
}

const MaintenancesForm = () => {
    const { id } = useParams();
    const navigate = navigateHook();
    const [searchParams] = useSearchParams();
    const cp = searchParams.get("cp");

    const [state, setState] = useState(initialState);
    const [reviewItems, setReviewItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const { data, isLoadingFetch, refetch } = useQuery({
        queryFn: () => maintenancesService.getById(id),
        queryKey: ["maintenance", id],
        enabled: !!id,
    });

    const { data: reviewItemsFetch } = useQuery({
        queryFn: () => maintenancesService.reviewItems(),
        queryKey: ["reviewItems"],
    });

    const {vehicles, conductors, mechanics} = useFetchData(id);

    useEffect(() => {
        if (!!id && data) {
            const mechanics = JSON.parse(data.mechanics) || {};

            setState({
                vehicles_id: data.vehicles_id,
                conductor: data.conductor,
                date_start: data.date_start,
                date_end: data.date_end,
                value_pay: data.value_pay ?? "",
                km_start: data.km_start,
                km_end: data.km_end,
                description: data.description,
                mechanic_file: data.mechanic_file,
                mechanics: mechanics?.id,
                schedule_maintenance: [true, 1, "1", "true"].includes(data.schedule_maintenance),
                schedule_cancel: data.schedule_cancel,
                mechanic_file_attach: null,
            });
        } else  {
            setState(initialState);
        }
    }, [id, data]);

    useEffect(() => {
        if(!cp) return;

        setState(prev => ({...prev, vehicles_id: cp}))
    }, [cp]);

    useEffect(() => {
        if (!reviewItemsFetch) return;

        let items = reviewItemsFetch.map(item => ({km: false, next_km: "", next_date: "", description: item.description, description_id: item.id}));

        if (!!id && data) {
            const itemsFetch = JSON.parse(data.review_items);

            items = items.map(item => {
                const match = itemsFetch.find(f => f.description_id === item.description_id);
                return match ? { ...item, ...match } : item;
            });
        }

        setReviewItems(items);
    }, [id, data, reviewItemsFetch]);

    const handleChange = (value, field) => {
        setState(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleVehicleChange = (select) => {
        setState(prev => ({
            ...prev,
            vehicles_id: select?.value ?? null,
            km_start: select?.actual_km ?? prev.km_start
        }));
    };

    const handleReviewItem = (value, field, id) => {
        setReviewItems(prev => prev.map(item => item.description_id === id ? {...item, [field]: value} : item));
    };

    const save = () => {
        const formData = new FormData();

        if (state.mechanic_file_attach?.file instanceof File) {
            formData.append('mechanic_file_attach', state.mechanic_file_attach?.file);
        }

        const items = reviewItems.map(item => {
            item.next_date = projectUtils.formatDate(item.next_date ? new Date(item.next_date) : "")
            return item;
        });

        formData.append('date_start', state.date_start ? projectUtils.formatDate(new Date(state.date_start)) : "");
        formData.append('date_end', state.date_end ? projectUtils.formatDate(new Date(state.date_end)) : "");
        formData.append('value_pay', state.value_pay);
        formData.append('km_start', state.km_start);
        formData.append('km_end', state.km_end ?? "");
        formData.append('description', state.description);
        formData.append('mechanics_id', state.mechanics ?? "");

        let scheduleMaintenance = state.schedule_maintenance;
        if (!(state.date_start && new Date(state.date_start) >= new Date())) {
            scheduleMaintenance = false
        }

        formData.append('schedule_maintenance', scheduleMaintenance ? '1' : '0');
        formData.append('schedule_cancel', state.schedule_cancel);

        formData.append('review_items', JSON.stringify(items));

        setIsLoading(true);

        let response;
        if (id) {
            if (state.mechanic_file) formData.append('mechanic_file', state.mechanic_file);
            formData.append('_method', 'PUT');
            response = maintenancesService.update(id, formData);
        }
        else {
            formData.append('vehicles_id', state.vehicles_id ?? "");
            formData.append('conductor', state.conductor ?? "");

            response = maintenancesService.create(formData);
        }

        response
            .then(({data}) => {
                projectUtils.success(!id);

                if (id) {
                    refetch();
                    return;
                }

                const urlDev = `/maintenances/form/${data.id}`;
                navigate(urlDev);
            })
            .catch((error) => {
                console.error(error);
                let message = 'Não foi possível salvar.';
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }

                projectUtils.swalErrorMessage('', message, 'Ok');
            }).finally(() => setIsLoading(false))
    }

    const deleteMaintenance = async () => {
        projectUtils.swalMessage(
            "warning",
            "Excluir",
            `Excluir a manutenção?`,
            "Sim",
            true,
            "Não",
            ({isConfirmed}) => {
                if (isConfirmed) {
                    setIsLoading(true);

                    maintenancesService.delete(id)
                        .then(() => {
                            projectUtils.success(true, "Excluído com sucesso");
                            navigate("/maintenances");
                        })
                        .catch((error) => {
                            console.error(error);
                            let message = 'Não foi possível salvar.';
                            if (error.response.status !== 500) {
                                message = error.response.data.message;
                            }

                            projectUtils.swalErrorMessage('', message, 'Ok');
                        }).finally(() => setIsLoading(false))
                }
            }
        );
    };

    return (
        <Template isLoading={isLoadingFetch || isLoading}>
            <Template.Header>
                <Template.Title>Manutenção</Template.Title>

                {
                    id &&
                    <Button
                        target="primary-outline"
                        onClick={() => navigate("/maintenances/form")}
                    >
                        <FaPlus /> Adicionar manutenção
                    </Button>
                }
            </Template.Header>

            <Template.Body className={"space-y-2"}>
                <Card>
                    <Card.Body>
                        <SentryRow>
                            {
                                id && data &&
                                <>
                                    <InputForm
                                        id={"vehicle"}
                                        classNameWrapper={"col-span-4"}
                                        defaultValue={data.vehicle}
                                        disabled
                                        label="Veículo *"
                                    />
                                    <InputForm
                                        id={"conductor"}
                                        classNameWrapper={"col-span-4"}
                                        defaultValue={data.conductors?.name ?? ""}
                                        disabled
                                        label="Responsável *"
                                    />
                                </>
                            }

                            {
                                !id &&
                                <>
                                    <SelectComponent
                                        label="Veículo *"
                                        options={vehicles}
                                        value={state.vehicles_id}
                                        classNameWrapper={"col-span-4"}
                                        onChange={handleVehicleChange}
                                    />
                                    <SelectComponent
                                        label="Responsável *"
                                        options={conductors}
                                        value={state.conductor}
                                        classNameWrapper={"col-span-4"}
                                        onChange={(v) => handleChange(v?.value, "conductor")}
                                    />
                                </>
                            }

                            <SelectComponent
                                label="Oficina *"
                                options={mechanics}
                                value={state.mechanics}
                                classNameWrapper={"col-span-4"}
                                onChange={(v) => handleChange(v?.value, "mechanics")}
                            />

                            <InputDatetime
                                id="date_start"
                                label="Início da manutenção *"
                                selected={state.date_start}
                                onChange={(v) => handleChange(v, "date_start")}
                                classNameWrapper="col-span-2"
                            />

                            <InputDatetime
                                id="date_end"
                                label="Término da manut."
                                selected={state.date_end}
                                onChange={(v) => handleChange(v, "date_end")}
                                classNameWrapper="col-span-2"
                            />

                            <InputForm
                                id={"value_pay"}
                                value={state.value_pay}
                                onChange={({target}) => handleChange(target.value, "value_pay")}
                                label={"Valor pago"}
                                type={"number"}
                                classNameWrapper="col-span-2"
                            />

                            <InputForm
                                id={"km_start"}
                                value={state.km_start}
                                onChange={({target}) => handleChange(target.value, "km_start")}
                                label={"Km na entrada *"}
                                integerOnly
                                type={"number"}
                                classNameWrapper="col-span-3"
                            />

                            <InputForm
                                id={"km_end"}
                                value={state.km_end}
                                onChange={({target}) => handleChange(target.value, "km_end")}
                                label={"Km na saída" + (id? " *" : "")}
                                integerOnly
                                type={"number"}
                                classNameWrapper="col-span-3"
                            />

                            <InputCheckbox
                                id={"schedule_maintenance"}
                                checked={state.schedule_maintenance}
                                onChange={({target}) => handleChange(target.checked, "schedule_maintenance")}
                                label={"Deixar o veículo indisponível somente no dia agendado da manutenção"}
                                className="col-span-full"
                                disabled={!(state.date_start && new Date(state.date_start) >= new Date())}
                            />

                            {
                                reviewItems.map(item => (
                                    <SentryRow key={item.description_id} className={"!grid-cols-3 col-span-12 px-40"}>
                                        <InputCheckbox
                                            id={"km_" + item.description_id}
                                            checked={item.km}
                                            label={item.description}
                                            onChange={({target}) => handleReviewItem(target.checked, "km", item.description_id)}
                                        />

                                        <InputForm
                                            id={"next_km_" + item.description_id}
                                            value={item.next_km ?? ""}
                                            onChange={({target}) => handleReviewItem(target.value, "next_km", item.description_id)}
                                            label={"Próximo km da " + item.description}
                                            integerOnly
                                            type={"number"}
                                            disabled={!item.km}
                                        />

                                        <InputDatetime
                                            id={"next_date_" + item.description_id}
                                            selected={item.next_date ?? ""}
                                            onChange={(value) => handleReviewItem(value, "next_date", item.description_id)}
                                            label={"Próximo data da " + item.description}
                                            disabled={!item.km}
                                            dateFormat={"L"}
                                        />
                                    </SentryRow>
                                ))
                            }

                            <InputTextarea
                                id={"description"}
                                value={state.description}
                                onChange={({target}) => handleChange(target.value, "description")}
                                label={"Descrição *"}
                                className="col-span-12"
                                textAreaClass={"!h-25"}
                            />

                            <InputFile
                                file={(data && state.mechanic_file) ? {attachId: state.mechanic_file, extension: data.mechanic_file_ext} : state.mechanic_file_attach}
                                setFile={(file) => setState(prev => ({...prev, mechanic_file: null, mechanic_file_attach: file}))}
                                removeFile={() => setState(prev => ({...prev, mechanic_file: null, mechanic_file_attach: null}))}
                            />

                            {data?.schedule_cancel ? (
                                <Alert className="p-[5px_10px]! text-right col-start-7 col-span-5" target="warning">
                                    Manutenção cancelada, veículo indisponível na data da manutenção.
                                </Alert>
                            ) : null}
                        </SentryRow>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Body className={"flex justify-end gap-2"}>
                        {
                            id &&
                            <Button target="danger" onClick={deleteMaintenance} actionFeedbackType='delete'>

                                Excluir
                            </Button>
                        }
                        <Button onClick={save} actionFeedbackType='save'>

                            Salvar
                        </Button>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default MaintenancesForm;